import { useEffect, useState } from "react";
import { approveQuote, createQuote, listQuotes, sendQuote } from "../api/finance.ts";
import { useAuth } from "../context/auth.tsx";
import { Layout } from "../components/layout.tsx";

interface Quote {
  id: string;
  quoteNumber: string;
  clientId: string;
  title: string;
  total: number;
  status: string;
  validUntil: string | null;
  currency: string;
  version: number;
  createdAt: string;
}

const STATUS_COLOR: Record<string, string> = {
  DRAFT: "text-gray-400 bg-gray-800",
  PENDING_APPROVAL: "text-yellow-400 bg-yellow-900/30",
  APPROVED: "text-blue-400 bg-blue-900/30",
  SENT: "text-indigo-400 bg-indigo-900/30",
  ACCEPTED: "text-green-400 bg-green-900/30",
  REJECTED: "text-red-400 bg-red-900/30",
  EXPIRED: "text-gray-500 bg-gray-800",
};

function currency(n: number, c = "INR") {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: c,
    maximumFractionDigits: 0,
  }).format(n);
}

export function FinanceQuotesPage() {
  const { token } = useAuth();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    clientId: "",
    title: "",
    unitPrice: "",
    quantity: "1",
    taxRate: "0",
  });

  const reload = () => {
    void listQuotes(token, { limit: 50 }).then((d) => {
      setQuotes((d as { items: Quote[] }).items ?? []);
      setLoading(false);
    });
  };

  useEffect(() => {
    reload();
  }, []);

  const create = async () => {
    if (!form.title || !form.clientId || !form.unitPrice) return;
    setCreating(true);
    const qty = parseFloat(form.quantity) || 1;
    const price = parseFloat(form.unitPrice) || 0;
    await createQuote(token, {
      clientId: form.clientId,
      title: form.title,
      lineItems: [
        {
          id: "item-1",
          description: form.title,
          quantity: qty,
          unitPrice: price,
          amount: qty * price,
        },
      ],
      taxRate: parseFloat(form.taxRate) || 0,
    });
    setForm({ clientId: "", title: "", unitPrice: "", quantity: "1", taxRate: "0" });
    setShowForm(false);
    setCreating(false);
    reload();
  };

  const approve = async (q: Quote) => {
    if (!window.confirm(`Approve quote ${q.quoteNumber}?`)) return;
    await approveQuote(token, q.id, q.version);
    reload();
  };

  const send = async (q: Quote) => {
    await sendQuote(token, q.id, q.version);
    reload();
  };

  return (
    <Layout>
      <div className="mx-auto max-w-6xl p-6">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Quotations</h1>
          <button
            onClick={() => setShowForm(!showForm)}
            className="rounded bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-500"
          >
            {showForm ? "Cancel" : "New Quote"}
          </button>
        </div>
        {showForm && (
          <div className="mb-6 space-y-3 rounded-lg border border-gray-700 bg-gray-900 p-4">
            <h2 className="text-sm font-medium text-gray-300">New Quotation</h2>
            <div className="grid grid-cols-2 gap-3">
              <input
                value={form.clientId}
                onChange={(e) => setForm((f) => ({ ...f, clientId: e.target.value }))}
                placeholder="Client ID"
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500"
              />
              <input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Title / Description"
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500"
              />
              <input
                value={form.unitPrice}
                onChange={(e) => setForm((f) => ({ ...f, unitPrice: e.target.value }))}
                placeholder="Unit Price (₹)"
                type="number"
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500"
              />
              <input
                value={form.quantity}
                onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
                placeholder="Quantity"
                type="number"
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500"
              />
              <input
                value={form.taxRate}
                onChange={(e) => setForm((f) => ({ ...f, taxRate: e.target.value }))}
                placeholder="Tax Rate %"
                type="number"
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500"
              />
            </div>
            <button
              onClick={() => void create()}
              disabled={creating}
              className="rounded bg-green-700 px-4 py-2 text-sm text-white hover:bg-green-600 disabled:opacity-50"
            >
              {creating ? "Creating..." : "Create Quote"}
            </button>
          </div>
        )}
        {loading ? (
          <p className="text-gray-400">Loading...</p>
        ) : (
          <div className="space-y-3">
            {quotes.length === 0 && <p className="text-gray-400">No quotes yet.</p>}
            {quotes.map((q) => (
              <div key={q.id} className="rounded-lg border border-gray-800 bg-gray-900 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-mono text-xs text-gray-500">{q.quoteNumber}</span>
                    <span className="ml-3 font-medium text-white">{q.title}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-white">
                      {currency(q.total, q.currency)}
                    </span>
                    <span
                      className={`rounded px-2 py-0.5 text-xs ${STATUS_COLOR[q.status] ?? "bg-gray-800 text-gray-400"}`}
                    >
                      {q.status}
                    </span>
                  </div>
                </div>
                <div className="mt-2 flex gap-2">
                  {q.status === "DRAFT" && (
                    <button
                      onClick={() => void approve(q)}
                      className="rounded bg-blue-900/50 px-2 py-1 text-xs text-blue-300 hover:bg-blue-900"
                    >
                      Approve
                    </button>
                  )}
                  {q.status === "APPROVED" && (
                    <button
                      onClick={() => void send(q)}
                      className="rounded bg-indigo-900/50 px-2 py-1 text-xs text-indigo-300 hover:bg-indigo-900"
                    >
                      Send to Client
                    </button>
                  )}
                  {q.validUntil && (
                    <span className="text-xs text-gray-500">
                      Valid until {new Date(q.validUntil).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
